import { Button } from "@/components/ui";
import { springs } from "@/components/ui/motion";
import { getPaletteColor } from "@/lib/color-palettes";
import type { AuditDashboardMetrics } from "@/packages/types";
import { useTranslation } from "@/packages/utils";
import { motion } from "framer-motion";
import { Check, Plus, Shield } from "lucide-react";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface FindingsCardProps {
  findingStats?: AuditDashboardMetrics["findingStats"];
  onNavigateToFindings?: () => void;
  onCreateFinding?: () => void;
  index?: number;
}

function getSeverityColors() {
  return {
    critical: getPaletteColor("findings", 1),
    high: getPaletteColor("findings", 2),
    medium: getPaletteColor("findings", 3),
    low: getPaletteColor("findings", 4),
  };
}

/** Severity keys and their schema mapping */
const severitySchemaMap: Record<
  string,
  { key: keyof ReturnType<typeof getSeverityColors> }
> = {
  critical: { key: "critical" },
  major: { key: "high" },
  minor: { key: "medium" },
  observation: { key: "low" },
};

type SeverityKey = "critical" | "high" | "medium" | "low";

export function FindingsCard({
  findingStats,
  onNavigateToFindings,
  onCreateFinding,
  index,
}: FindingsCardProps) {
  const { t } = useTranslation("audits");

  if (!findingStats || findingStats.total === 0) {
    return (
      <WidgetCard
        size="auto"
        title={t("overview.findings.title")}
        icon={<Shield size={16} />}
        index={index}
        className="h-auto shrink-0"
      >
        <div className="flex flex-col items-center justify-center h-full gap-3 py-3">
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...springs.smooth, delay: 0.1 }}
          >
            {/* Soft pulsing ring behind the icon */}
            <motion.div
              className="absolute inset-0 rounded-full bg-accent/8"
              animate={{ scale: [1, 1.35, 1], opacity: [0.5, 0, 0.5] }}
              transition={{
                duration: 3,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
              }}
            />
            <div className="w-12 h-12 rounded-full bg-accent/[0.06] border border-accent/15 flex items-center justify-center">
              <Shield size={22} className="text-accent/50" />
            </div>
          </motion.div>

          <motion.div
            className="text-center space-y-0.5"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springs.smooth, delay: 0.15 }}
          >
            <p className="text-[13px] text-secondary font-medium">
              {t("overview.findings.noFindings")}
            </p>
            <p className="text-[11px] text-muted leading-snug">
              {t("overview.findings.noFindingsDescription")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springs.smooth, delay: 0.2 }}
          >
            <Button
              variant="accent-soft"
              size="sm"
              icon={Plus}
              onClick={(e) => {
                e.stopPropagation();
                (onCreateFinding ?? onNavigateToFindings)?.();
              }}
            >
              {t("overview.findings.addFirst")}
            </Button>
          </motion.div>
        </div>
      </WidgetCard>
    );
  }

  const colors = getSeverityColors();
  const segments: { key: SeverityKey; count: number; color: string }[] = (
    ["critical", "high", "medium", "low"] as const
  )
    .map((key) => ({
      key,
      count: findingStats[key],
      color: colors[key],
    }))
    .filter((s) => s.count > 0);

  const total = segments.reduce((sum, s) => sum + s.count, 0);

  let runningOffset = 0;
  const segmentsWithOffset = segments.map((seg) => {
    const width = (seg.count / total) * 100;
    const offset = runningOffset;
    runningOffset += width;
    return { ...seg, width, offset };
  });

  const resolvedCount = findingStats.resolved;
  const resolvedPct = total > 0 ? Math.round((resolvedCount / total) * 100) : 0;

  const getSeverityLabel = (schemaKey: string): string => {
    if (schemaKey === "critical") return t("findings.severity.critical");
    if (schemaKey === "major") return t("findings.severity.major");
    if (schemaKey === "minor") return t("findings.severity.minor");
    return t("findings.severity.observation");
  };

  const legendItems = Object.entries(severitySchemaMap)
    .filter(([, schema]) => findingStats[schema.key] > 0)
    .map(([schemaKey, schema]) => {
      const colorKey = schema.key as SeverityKey;
      return {
        key: colorKey,
        label: getSeverityLabel(schemaKey),
        color: colors[colorKey],
        count: findingStats[colorKey],
      };
    });

  return (
    <WidgetCard
      size="auto"
      title={t("overview.findings.title")}
      icon={<Shield size={16} />}
      index={index}
      className="h-auto shrink-0"
    >
      <div className="flex flex-col h-full">
        {/* Header row */}
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-lg font-semibold tabular-nums">
            {t("overview.findings.total", { count: findingStats.total })}
          </span>
          {resolvedCount > 0 && (
            <span className="text-xs text-muted flex items-center gap-0.5">
              {resolvedCount} {t("overview.findings.resolved")}
              <Check size={12} className="text-emerald-400" />
            </span>
          )}
        </div>

        {/* Thick severity bar */}
        <svg
          width="100%"
          height="24"
          className="rounded-md overflow-hidden flex-shrink-0"
          role="img"
          aria-label={t("overview.findings.severityDistribution")}
        >
          <defs>
            <linearGradient id="glass-overlay" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="white" stopOpacity="0.25" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>
          {segmentsWithOffset.map((seg, i) => (
            <g key={seg.key}>
              <motion.rect
                x={`${seg.offset}%`}
                y={0}
                height={24}
                fill={seg.color}
                rx={i === 0 || i === segmentsWithOffset.length - 1 ? 6 : 0}
                initial={{ width: 0 }}
                animate={{ width: `${seg.width}%` }}
                transition={{ ...springs.smooth, delay: 0.2 + i * 0.05 }}
              />
              <motion.rect
                x={`${seg.offset}%`}
                y={0}
                height={12}
                fill="url(#glass-overlay)"
                rx={i === 0 || i === segmentsWithOffset.length - 1 ? 6 : 0}
                initial={{ width: 0 }}
                animate={{ width: `${seg.width}%` }}
                transition={{ ...springs.smooth, delay: 0.2 + i * 0.05 }}
              />
            </g>
          ))}
        </svg>

        {/* Severity legend */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
          {legendItems.map((item) => (
            <div key={item.key} className="flex items-center gap-1">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs text-muted tabular-nums">
                {item.count} {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* Resolved progress bar */}
        {resolvedCount > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-3.5 rounded-full bg-border/50 overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: "#4ade80" }}
                initial={{ width: 0 }}
                animate={{ width: `${resolvedPct}%` }}
                transition={{ ...springs.smooth, delay: 0.4 }}
              />
            </div>
            <span className="text-xs text-muted tabular-nums whitespace-nowrap">
              {t("overview.findings.resolvedPct", { pct: resolvedPct })}
            </span>
          </div>
        )}

        {/* View all button */}
        <div className="mt-3 pt-2.5 border-t border-border/40">
          <Button
            variant="accent-soft"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onNavigateToFindings?.();
            }}
          >
            {t("overview.findings.viewAll")}
          </Button>
        </div>
      </div>
    </WidgetCard>
  );
}

export function FindingsCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="auto" index={index} />;
}
